import { ArrowLeft } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  ListItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import React, { useContext, useState } from "react";
import TagManager from "react-gtm-module";
import MapContext from "../../Contexts/MapContext";
import UserContext from "../../Contexts/UserContext";

function CategoryFilter({ options, onChange, value }) {
  const [selected, setSelected] = useState(value || []);
  const [viewingMore, setViewingMore] = useState(false);
  const _value = value || selected;
  const _onChange = (e, option, checked) => {
    setSelected((oldValue) => {
      const newValue = checked
        ? Array.from(new Set([...oldValue, option]))
        : oldValue.filter((s) => s !== option);
      onChange?.(e, newValue);
      return newValue;
    });
  };
  const maxVisibleCategories = 5;

  return (
    <Stack direction={"row"} alignItems={"flex-start"}>
      {!viewingMore && (
        <Stack direction={"row"} spacing={1} flex={1} overflow={"auto"}>
          {(options || []).slice(0, maxVisibleCategories).map((option) => (
            <Chip
              label={option}
              variant={_value.includes(option) ? "filled" : "outlined"}
              color={_value.includes(option) ? "primary" : undefined}
              onClick={(e) => _onChange(e, option, !_value.includes(option))}
            />
          ))}
        </Stack>
      )}
      {viewingMore && (
        <Autocomplete
          sx={{ width: "100%" }}
          multiple
          limitTags={2}
          options={options}
          onChange={(event, option) => {
            setSelected(option);
            onChange?.(event, option);
          }}
          value={_value}
          size="small"
          open
          includeInputInList
          disableCloseOnSelect
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{ ...params.inputProps, style: { fontSize: "16px" } }}
              label="Category"
              variant="filled"
              fullWidth
              size="small"
              name={`filter-product-category`}
            />
          )}
          renderOption={(props, option, state, ownerState) => (
            <ListItem {...props} dense sx={{ fontSize: 14 }}>
              <Checkbox style={{ marginRight: 8 }} checked={state.selected} />
              {ownerState.getOptionLabel
                ? ownerState.getOptionLabel(option)
                : option}
            </ListItem>
          )}
        />
      )}
      {!viewingMore && options?.length > maxVisibleCategories && (
        <Button
          variant="text"
          color="inherit"
          size="small"
          onClick={() => setViewingMore(true)}
          sx={{ marginTop: "3px" }}
        >
          more
        </Button>
      )}
      {viewingMore && (
        <Stack direction={"column"}>
          <Button
            variant="text"
            color="inherit"
            onClick={(event) => {
              setSelected([]);
              onChange?.(event, []);
              setViewingMore(false);
            }}
          >
            Clear
          </Button>
          <Button
            variant="text"
            color="inherit"
            onClick={() => setViewingMore(false)}
          >
            Done
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

export default function ProductFilterContent({
  selectedProducts = [],
  setSelectedProducts,
  isStrictSelection,
}) {
  const [searchValue, setSearchValue] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [failedImages, setFailedImages] = useState([]);
  const [viewingSelected, setViewingSelected] = useState(false);
  const [strictSelection, setStrictSelection] = useState(
    isStrictSelection || false
  );
  const [_selectedProducts, _setSelectedProducts] = useState(selectedProducts);
  const mapContext = useContext(MapContext);
  const userContext = useContext(UserContext);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const minProductsForCategories = +(
    userContext.state?.config?.minProductsForCategories || 5
  );
  const storeLabel =
    userContext.state?.orgProperties?.storeNameReplacement || "Store";
  const validProducts = mapContext.products || [];
  const products = [
    ...(userContext.state?.config?.productPriority
      ?.map((id) => validProducts.find((p) => p.id === id))
      .filter((p) => !!p) || []),
    ...validProducts.filter(
      (p) => !userContext.state?.config?.productPriority?.includes(p.id)
    ),
  ].filter(
    (p) =>
      (viewingSelected
        ? _selectedProducts.includes(p.id)
        : !selectedCategories.length ||
          selectedCategories.includes(p.category)) &&
      JSON.stringify(p).toLowerCase().includes(searchValue.toLowerCase())
  );
  const categoryOptions = Array.from(
    new Set(
      [...(products || []), ...(mapContext.products || [])]
        .map((p) => p.category)
        .filter((c) => !!c)
    )
  );
  const allSelected = products.every((p) => _selectedProducts.includes(p.id));
  const allIsIndeterminate = !allSelected && _selectedProducts.length > 0;

  const getWidth = () => {
    if (isMobile) {
      return undefined;
    }
    switch (userContext.state?.config?.productFilterSize) {
      case "small":
        return "25rem";
      case "medium":
        return "45rem";
      case "large":
        return "60rem";
      default:
        return "25rem";
    }
  };

  const getGridSize = () => {
    if (isMobile) {
      return 12;
    }
    switch (userContext.state?.config?.productFilterSize) {
      case "small":
        return 6;
      case "medium":
        return 4;
      case "large":
        return 4;
      default:
        return 6;
    }
  };

  const getGridMaxHeight = () => {
    if (isMobile) {
      return "100%";
    }
    switch (userContext.state?.config?.productFilterSize) {
      case "small":
        return "20rem";
      case "medium":
        return "25rem";
      case "large":
        return "100%";
      default:
        return "20rem";
    }
  };
  return (
    <Stack
      direction={"column"}
      p={2}
      spacing={isMobile ? 1 : 2}
      maxWidth={getWidth()}
      maxHeight={isMobile ? "calc(100dvh - 35px)" : "calc(100vh - 35px)"}
      width={isMobile ? undefined : getWidth()}
      sx={isMobile ? { "@supports not (height: 100dvh)": { maxHeight: "calc(100vh - 35px)" } } : undefined}
    >
      {userContext.state?.config?.productFilterType !== "category" && !viewingSelected && (
        <TextField
          label="Search for product"
          variant="outlined"
          fullWidth
          size="small"
          name={`filter-Product-search`}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          inputProps={{ style: { fontSize: "16px" } }}
        />
      )}
      {!viewingSelected &&
        !!categoryOptions.length &&
        userContext.state?.config?.productFilterType !== "category" &&
        validProducts.length >= minProductsForCategories && (
          <CategoryFilter
            options={categoryOptions}
            onChange={(event, option) => {
              setSelectedCategories(option);
              TagManager.dataLayer({
                dataLayer: {
                  event: "flavorFilterChange",
                  category: "Product Category Filter",
                  label: option,
                  data: option,
                  snowplowParameters: {
                    terms: [`${option}`, `${option}`],
                    filters: {
                      "Product-category-Filter": `${option}`,
                    },
                  },
                },
              });
            }}
            value={selectedCategories}
          />
        )}
      {userContext.state?.config?.productFilterType !== "category" &&
        <Stack direction={"row"} justifyContent={"space-between"}>
          {!viewingSelected && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={products.every((p) =>
                    _selectedProducts.includes(p.id)
                  )}
                  indeterminate={allIsIndeterminate}
                  onChange={(e, checked) => {
                    _setSelectedProducts?.(
                      checked
                        ? Array.from(
                          new Set([
                            ..._selectedProducts,
                            ...products.map((p) => p.id),
                          ])
                        )
                        : _selectedProducts.filter(
                          (s) => !products.map((p) => p.id).includes(s)
                        )
                    );
                  }}
                />
              }
              label="Select all"
            />
          )}
          {!!viewingSelected && (
            <Button
              variant="text"
              color="inherit"
              onClick={() => setViewingSelected(false)}
              startIcon={<ArrowLeft />}
            >
              Product search
            </Button>
          )}
          {!!_selectedProducts.length && !viewingSelected && (
            <Button
              variant="text"
              color="inherit"
              onClick={() => setViewingSelected(true)}
            >
              {_selectedProducts.length} selected
            </Button>
          )}
          {(!!searchValue.length || !!selectedCategories.length) && (
            <Button
              variant="text"
              color="inherit"
              onClick={() => {
                setSearchValue("");
                setSelectedCategories([]);
              }}
            >
              Clear filters
            </Button>
          )}
        </Stack>}
      {!userContext.state.config?.hideAvailabilityMessage && (
        <Typography variant="subtitle2">
          {userContext.state.config?.customAvailabilityMessage ||
            "Always check availability with store"}
        </Typography>
      )}
      <Grid
        container
        spacing={2}
        flex={1}
        minHeight={0}
        maxHeight={getGridMaxHeight()}
        overflow={"auto"}
        sx={{
          "&::-webkit-scrollbar": {
            width: "10px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "10px",
            border: "3px solid #f1f1f1"
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {products?.reduce((acc, productItem) => {
          if (userContext.state?.config?.productFilterType === "category") {
            if (productItem.category && !acc.find(i => i.id === productItem.category)) {
              return [...acc, {id: productItem.category, name: productItem.category, url: userContext.state?.config?.productCategoryImages[productItem.category]}]
            }
            return acc
          }
          return [...acc, productItem]
        }, []).map((productItem) => (
          <Grid key={productItem.id} xs={getGridSize()} item>
            <Paper
              style={{
                overflow: "hidden",
                position: "relative",
                height: "100%",
              }}
            >
              <Checkbox
                checked={userContext.state?.config?.productFilterType === "category" ? !!products.filter(p => _selectedProducts.includes(p.id) && p.category === productItem.id).length : _selectedProducts.includes(productItem.id)}
                onChange={(e, checked) => {
                  const newSelection = Array.from(
                    new Set([
                      ..._selectedProducts.filter((s) => userContext.state?.config?.productFilterType === "category" ? !products.filter(p => p.category === productItem.id).map(p => p.id).includes(s) : s !== productItem.id),
                      ...(checked ? (userContext.state?.config?.productFilterType === "category" ? products.filter(p => p.category === productItem.id).map(p => p.id) : [productItem.id]) : []),
                    ])
                  );
                  if (newSelection.length === 0) setViewingSelected(false);
                  _setSelectedProducts?.(newSelection);
                }}
                style={{ position: "absolute", top: 0, left: 0 }}
              />
              <Stack
                direction={"column"}
                alignItems={"center"}
                spacing={1}
                p={1}
                height={"100%"}
              >
                {failedImages.includes(productItem.id) || !productItem?.url ? (
                  <Typography
                    color={"tomato"}
                    border={"1px solid tomato"}
                    borderRadius={1}
                    p={1}
                    bgcolor="whitesmoke"
                    width={isMobile ? 60 : 80}
                    style={{ maxHeight: 250 }}
                  >
                    No Image
                  </Typography>
                ) : (
                  <img
                    alt="product"
                    src={productItem?.url}
                    width={isMobile ? 60 : 80}
                    style={{ maxHeight: 250, objectFit: "contain" }}
                    onError={() =>
                      setFailedImages((old) => [...old, productItem.id])
                    }
                  />
                )}
                <Typography variant="body2">
                  {userContext?.formatText(productItem?.name)}
                </Typography>
                {!!productItem?.category && (
                  <Typography variant="caption" alignContent={"end"} flex={1}>
                    {userContext?.formatText(productItem?.category)}
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Stack
        direction={"row"}
        flexShrink={0}
        sx={isMobile ? { pb: "env(safe-area-inset-bottom)" } : undefined}
      >
        {/* <FormControlLabel
          control={
            <Checkbox
              checked={!!strictSelection}
              onChange={(e, checked) => {
                setStrictSelection(!!checked);
              }}
            />
          }
          label={`${storeLabel} must have all selected products`}
        /> */}
        <Button
          fullWidth
          variant="contained"
          {...(userContext.state?.config?.productFilterBtnProps  || {})}
          onClick={() =>
            setSelectedProducts(_selectedProducts, strictSelection)
          }
        >
          See {storeLabel}s
        </Button>
      </Stack>
    </Stack>
  );
}
